import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { delay, mergeMap } from 'rxjs/operators';

/**
 * Custom preloading strategy that selectively preloads modules based on data properties
 */
@Injectable({
  providedIn: 'root'
})
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  /**
   * Selectively preloads routes based on the `preload` property set in the route data
   * Priority values in the preload property determine delay:
   * - high: Preload immediately (0ms delay)
   * - medium: Preload after short delay (1000ms)
   * - low: Preload after longer delay (3000ms)
   */
  preload(route: Route, load: () => Observable<any>): Observable<any> {
    // Only preload if the route has indicated it should be preloaded
    if (route.data && route.data['preload']) {
      // Determine the delay based on priority
      let delayTime = 0;
      
      if (typeof route.data['preload'] === 'string') {
        switch(route.data['preload']) {
          case 'high':
            delayTime = 0;
            break;
          case 'medium':
            delayTime = 1000;
            break;
          case 'low':
            delayTime = 3000;
            break;
          default:
            return of(null); // Don't preload if priority is not recognized
        }
      }
      
      console.log(`Preloading: ${route.path} with priority ${route.data['preload']}`);
      
      // Preload with appropriate delay
      return of(true).pipe(
        delay(delayTime),
        mergeMap(() => load())
      );
    }
    
    // Don't preload if not specified
    return of(null);
  }
}

/**
 * Network-aware preloading strategy that only preloads when the network is not limited
 */
@Injectable({
  providedIn: 'root'
})
export class NetworkAwarePreloadStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<any>): Observable<any> {
    // Check if route is marked for preloading
    if (route.data && route.data['preload']) {
      // Check connection quality
      const connection = (navigator as any).connection;
      
      if (!connection) {
        // If network info isn't available, only preload high priority routes
        if (route.data['preload'] === 'high') {
          return load();
        }
        return of(null);
      }
      
      // Check if we're on a slow connection or if data saver is enabled
      if (connection.saveData || 
          connection.effectiveType === 'slow-2g' || 
          connection.effectiveType === '2g') {
        // Don't preload anything on slow connections or when data saver is enabled
        return of(null);
      }
      
      // On 3G, only preload high priority
      if (connection.effectiveType === '3g' && route.data['preload'] !== 'high') {
        return of(null);
      }
      
      // On good connections, preload according to priority
      return load();
    }
    
    // Don't preload routes not marked for preloading
    return of(null);
  }
}

/**
 * QuickLink-inspired preloading strategy that preloads visible links and anticipated routes
 */
@Injectable({
  providedIn: 'root'
})
export class SmartPreloadStrategy implements PreloadingStrategy {
  private routeTriggers = new Map<string, boolean>();
  
  // Set a route as triggered for preload
  setTriggerRoute(path: string): void {
    this.routeTriggers.set(path, true);
  }
  
  preload(route: Route, load: () => Observable<any>): Observable<any> {
    // Always preload high priority routes
    if (route.data && route.data['preload'] === 'high') {
      return load();
    }
    
    // Check if route path has been triggered
    const routePath = route.path || '';
    
    if (this.routeTriggers.has(routePath)) {
      this.routeTriggers.delete(routePath);
      return load();
    }
    
    // Don't preload other routes
    return of(null);
  }
}